import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Check, AlertCircle, Globe, CheckCircle, Clock, Sparkles, ArrowLeft } from 'lucide-react';
import tourService from '../services/tourService';
import { useAuth } from '../contexts/AuthContext';
import { checkCredits, deductCredit } from '../services/creditsService';
import { supabase } from '../config/supabase';

interface LoadingStep {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const GeneratingTour: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [pendingTourId, setPendingTourId] = useState<string | null>(null);
  
  const startTimeRef = useRef<number>(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const hasStartedRef = useRef<boolean>(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // Get location, interests, and selected places from navigation state
  const { coordinates, interests, geocodeData, selectedPlaces, nearbyPlaces } = location.state || {};

  // 3 stages: Research steps only
  const steps: LoadingStep[] = [
    {
      id: 1,
      title: "Searching",
      description: "Finding info about your location and places",
      icon: <Search size={24} />,
    },
    {
      id: 2,
      title: "Researching",
      description: "Reading sources and gathering facts",
      icon: <Globe size={24} />,
    },
    {
      id: 3,
      title: "Writing",
      description: "Creating your personalized tour content",
      icon: <CheckCircle size={24} />,
    }
  ];

  // Format elapsed time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Start the elapsed time counter
  const startTimer = useCallback(() => {
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setElapsedTime(elapsed);
    }, 1000);
  }, []);
  
  // Progress through steps on a fixed schedule (independent of API)
  useEffect(() => {
    if (!isGenerating) return;
    
    // Get current elapsed time at the moment this effect runs
    const currentElapsedMs = (Date.now() - startTimeRef.current);
    
    // Only set timers for steps that haven't occurred yet
    const timers: NodeJS.Timeout[] = [];
    
    // Step 1: After 3 seconds, move to "Researching"
    if (currentElapsedMs < 3000) {
      const step1Timer = setTimeout(() => {
        setCompletedSteps([0]);
        setCurrentStep(1);
      }, 3000 - currentElapsedMs);
      timers.push(step1Timer);
    }
    
    // Step 2: After 25 seconds, move to "Writing"
    if (currentElapsedMs < 25000) {
      const step2Timer = setTimeout(() => {
        setCompletedSteps([0, 1]);
        setCurrentStep(2);
      }, 25000 - currentElapsedMs);
      timers.push(step2Timer);
    }
    
    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGenerating]); // Only run when isGenerating changes, not on every elapsedTime update

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      console.log('🧹 Cleaning up GeneratingTour component');
      if (timerRef.current) clearInterval(timerRef.current);
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  // Poll for tour completion when resuming
  const startPollingForCompletion = useCallback((tourId: string) => {
    if (pollingRef.current) return; // Already polling

    console.log('🔄 Starting to poll for tour completion');
    
    pollingRef.current = setInterval(async () => {
      try {
        const { data: tour, error } = await supabase
          .from('tours')
          .select('research_status')
          .eq('tour_id', tourId)
          .single();

        if (error) {
          console.error('Polling error:', error);
          return;
        }

        if (tour?.research_status === 'completed') {
          console.log('✅ Tour completed! Navigating...');
          
          // Trigger audio generation
          console.log('🎙️ Automatically triggering audio generation');
          tourService.generateAudio(tourId).catch(err => {
            console.error('Audio generation failed:', err);
          });
          
          // Clear polling
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          
          // Clear timers
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          
          // Clear localStorage
          localStorage.removeItem('pendingTourGeneration');
          
          // Navigate to tour
          navigate('/tour/' + tourId, { replace: true });
        } else if (tour?.research_status === 'failed') {
          console.error('❌ Tour generation failed');
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          setError('Tour generation failed');
          localStorage.removeItem('pendingTourGeneration');
        }
      } catch (err) {
        console.error('Polling exception:', err);
      }
    }, 5000); // Poll every 5 seconds
  }, [navigate]);

  useEffect(() => {
    if (!coordinates || !interests || !geocodeData || !selectedPlaces) {
      setError('Missing location, interests, or selected places data');
      setTimeout(() => navigate('/home'), 2000);
      return;
    }

    // Prevent duplicate calls FIRST (React strict mode, rerenders, etc.)
    if (hasStartedRef.current) {
      console.log('⛔ Already started, preventing duplicate execution');
      return;
    }

    // Check if we're returning to an existing generation
    const storedPendingTour = localStorage.getItem('pendingTourGeneration');
    if (storedPendingTour) {
      const pendingData = JSON.parse(storedPendingTour);
      console.log('📦 Found stored pending tour:', pendingData.tourId);
      
      // Check if this is the same generation (compare tour parameters)
      const isSameGeneration = 
        pendingData.coordinates?.latitude === coordinates.latitude &&
        pendingData.coordinates?.longitude === coordinates.longitude &&
        JSON.stringify(pendingData.interests?.sort()) === JSON.stringify(interests?.sort()) &&
        JSON.stringify(pendingData.selectedPlaces) === JSON.stringify(selectedPlaces);
      
      console.log('🔍 Comparing generations - Same?', isSameGeneration);
      console.log('  Coords match:', pendingData.coordinates?.latitude === coordinates.latitude);
      console.log('  Interests match:', JSON.stringify(pendingData.interests?.sort()) === JSON.stringify(interests?.sort()));
      
      if (isSameGeneration && pendingData.tourId) {
        // Mark as started to prevent re-execution
        hasStartedRef.current = true;
        
        // Resuming existing generation - check if it's completed
        console.log('✅ Resuming existing generation:', pendingData.tourId);
        setPendingTourId(pendingData.tourId);
        setIsGenerating(true);
        
        // Calculate elapsed time since generation started
        const startTime = pendingData.startTime || Date.now();
        const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
        setElapsedTime(elapsedSeconds);
        startTimeRef.current = startTime;
        
        // Start timer from current elapsed time
        timerRef.current = setInterval(() => {
          const elapsed = Math.floor((Date.now() - startTime) / 1000);
          setElapsedTime(elapsed);
        }, 1000);
        
        // Set step indicators based on elapsed time
        if (elapsedSeconds >= 45) {
          setCompletedSteps([0, 1, 2]);
          setCurrentStep(2);
        } else if (elapsedSeconds >= 25) {
          setCompletedSteps([0, 1]);
          setCurrentStep(2);
        } else if (elapsedSeconds >= 3) {
          setCompletedSteps([0]);
          setCurrentStep(1);
        } else {
          setCurrentStep(0);
        }
        
        console.log(`⏱️ Resuming at ${elapsedSeconds} seconds, step ${elapsedSeconds >= 25 ? 2 : elapsedSeconds >= 3 ? 1 : 0}`);
        
        // Check tour status in database
        supabase
          .from('tours')
          .select('research_status, title, narration, audio_status')
          .eq('tour_id', pendingData.tourId)
          .single()
          .then(({ data: tour, error }) => {
            if (error || !tour) {
              console.error('❌ Failed to check tour status:', error);
              // Clear and restart
              localStorage.removeItem('pendingTourGeneration');
              hasStartedRef.current = false;
              setIsGenerating(false);
              generateTourWithPerplexity();
              return;
            }

            console.log('📊 Tour status:', tour.research_status);

            if (tour.research_status === 'completed') {
              // Tour completed while user was away - navigate to it
              console.log('✅ Tour completed while away, navigating to tour');
              
              // Trigger audio generation if not already started
              if (!tour.audio_status || tour.audio_status === 'pending') {
                console.log('🎙️ Automatically triggering audio generation for resumed tour');
                tourService.generateAudio(pendingData.tourId).catch(err => {
                  console.error('Audio generation failed:', err);
                });
              }
              
              localStorage.removeItem('pendingTourGeneration');
              if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
              }
              navigate('/tour/' + pendingData.tourId, { replace: true });
            } else if (tour.research_status === 'pending') {
              // Still pending - show UI and start polling
              console.log('⏳ Tour still pending, showing UI and polling');
              startPollingForCompletion(pendingData.tourId);
            } else {
              // Failed or unknown state
              console.error('❌ Tour in unexpected state:', tour.research_status);
              localStorage.removeItem('pendingTourGeneration');
              setError('Tour generation failed');
            }
          });
        
        return;
      } else {
        // Different generation - clear old one
        console.log('🔄 Different generation detected, clearing old state');
        localStorage.removeItem('pendingTourGeneration');
      }
    }

    // Start new generation
    console.log('🆕 Starting new generation');
    hasStartedRef.current = true;
    generateTourWithPerplexity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordinates, interests, geocodeData, selectedPlaces]);

  const generateTourWithPerplexity = async () => {
    console.log('🚀 generateTourWithPerplexity called, isGenerating:', isGenerating);
    
    if (isGenerating) {
      console.log('⛔ Already generating, skipping');
      return;
    }
    
    setIsGenerating(true);
    startTimer();

    try {
      setCurrentStep(0);

      // Ensure user is authenticated
      if (!user) {
        console.error('User not authenticated');
        navigate('/');
        return;
      }

      // Use real authenticated user ID
      const userId = user.id;

      // New generation - proceed with credit check and deduction
      // 🔥 STEP 1: Check if user has credits
      console.log('Checking credits for authenticated user:', userId);
      const creditsData = await checkCredits(userId);
      
      if (!creditsData.has_credits || creditsData.total_credits < 1) {
        // User doesn't have credits - redirect to buy credits
        console.log('Insufficient credits:', creditsData);
        
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        
        navigate('/buy-credits', {
          replace: true,
          state: { 
            message: 'Your balance is insufficient, please\npurchase credits and try again.',
            returnTo: '/location' 
          }
        });
        return;
      }

      // 🔥 STEP 2: Deduct 1 credit BEFORE generating
      console.log('Deducting 1 credit from:', creditsData.total_credits);
      const deductResult = await deductCredit(userId, 1);
      
      if (deductResult.status === 'error') {
        // Failed to deduct credit
        console.error('Failed to deduct credit:', deductResult);
        throw new Error(deductResult.message || 'Failed to deduct credit');
      }

      console.log('✅ Credit deducted successfully. New balance:', deductResult.new_credits);

      // Refresh credits in the global state
      if ((window as any).refreshCredits) {
        (window as any).refreshCredits();
      }

      // 🔥 STEP 3: Create a pending tour in the database
      const newPendingTourId = `tour_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const { error: createError } = await supabase
        .from('tours')
        .insert({
          tour_id: newPendingTourId,
          user_id: userId,
          title: 'Researching...',
          description: 'This tour is currently being researched',
          narration: '',
          location_data: geocodeData,
          street_name: geocodeData.streetName,
          city: geocodeData.city,
          country: geocodeData.country,
          latitude: coordinates.latitude,
          longitude: coordinates.longitude,
          formatted_address: geocodeData.formattedAddress,
          estimated_duration: 0,
          distance: '0 km',
          audio_status: 'pending',
          research_status: 'pending',
          selected_places: selectedPlaces,
          nearby_places: nearbyPlaces || [],
          interests: interests,
          is_public: true  // Public by default so tours appear on community map
        });

      if (createError) {
        console.error('Failed to create pending tour:', createError);
        throw new Error('Failed to create pending tour');
      }

      setPendingTourId(newPendingTourId);
      console.log('✅ Created pending tour in database:', newPendingTourId);
      
      // Store generation state in localStorage
      localStorage.setItem('pendingTourGeneration', JSON.stringify({
        tourId: newPendingTourId,
        coordinates,
        interests,
        geocodeData,
        selectedPlaces,
        nearbyPlaces,
        startTime: Date.now(),
        userId
      }));

      // 🔥 STEP 4: Generate the tour (credit already deducted, tour already created)
      const tourData = await tourService.generatePerplexityTour(
        { ...geocodeData, latitude: coordinates.latitude, longitude: coordinates.longitude },
        selectedPlaces,
        interests,
        nearbyPlaces || [],
        user?.id,
        undefined,
        newPendingTourId // Pass the pending tour ID to update it
      );

      // Mark all 3 steps complete
      setCompletedSteps([0, 1, 2]);
      setCurrentStep(2);
      
      await new Promise(resolve => setTimeout(resolve, 800));

      // Clear pending tour from localStorage
      localStorage.removeItem('pendingTourGeneration');

      // Stop timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      // 🔥 STEP 5: Automatically trigger audio generation
      console.log('🎙️ Automatically triggering audio generation for tour:', tourData.tourId);
      try {
        // Trigger audio generation (don't await - let it run in background)
        tourService.generateAudio(tourData.tourId).catch(err => {
          console.error('Audio generation failed:', err);
          // Don't block navigation on audio failure
        });
      } catch (err) {
        console.error('Failed to trigger audio generation:', err);
        // Don't block navigation
      }

      // Navigate to tour page (replace history so back button doesn't return here)
      navigate('/tour', {
        replace: true,
        state: {
          tourId: tourData.tourId,
          tourData: {
            ...tourData,
            audioStatus: 'processing', // Mark as processing immediately
            locationData: {
              ...tourData.locationData,
              latitude: coordinates.latitude,
              longitude: coordinates.longitude
            }
          },
          location: geocodeData.formattedAddress,
          researchTime: elapsedTime
        }
      });

    } catch (err) {
      console.error('Perplexity tour generation error:', err);
      
      if (timerRef.current) clearInterval(timerRef.current);
      
      // Don't clear pending tour from localStorage on error, so they can resume
      
      setError(err instanceof Error ? err.message : 'Failed to generate tour');
    }
  };

  // Check for existing pending tour on mount
  useEffect(() => {
    const storedPendingTour = localStorage.getItem('pendingTourGeneration');
    if (storedPendingTour) {
      const pendingData = JSON.parse(storedPendingTour);
      setPendingTourId(pendingData.tourId);
    }
  }, []);

  const getStepState = (stepIndex: number) => {
    if (completedSteps.includes(stepIndex)) return 'completed';
    if (stepIndex === currentStep) return 'active';
    return 'upcoming';
  };

  const getStepStyles = (stepIndex: number) => {
    const state = getStepState(stepIndex);
    
    switch (state) {
      case 'completed':
        return {
          border: '2px solid var(--success-color)',
          boxShadow: '0 4px 16px rgba(34, 197, 94, 0.1)',
          filter: 'brightness(0.95)'
        };
      case 'active':
        return {
          border: '2px solid var(--primary-color)',
          boxShadow: '0 8px 32px rgba(99, 102, 241, 0.2)',
          filter: 'brightness(1)'
        };
      default:
        return {
          border: '2px solid var(--border-color)',
          boxShadow: 'none',
          filter: 'brightness(0.9)'
        };
    }
  };

  const getIconStyles = (stepIndex: number) => {
    const state = getStepState(stepIndex);
    
    switch (state) {
      case 'completed':
        return {
          backgroundColor: 'var(--success-color)',
          color: 'white'
        };
      case 'active':
        return {
          backgroundColor: 'var(--primary-color)',
          color: 'white',
          boxShadow: '0 8px 32px rgba(99, 102, 241, 0.4)'
        };
      default:
        return {
          backgroundColor: 'var(--border-color)',
          color: 'var(--text-secondary)'
        };
    }
  };

  if (error) {
    return (
      <div className="app">
        <div className="container flex-center" style={{ height: '100vh', padding: '40px 20px' }}>
          <div className="text-center" style={{ maxWidth: '400px' }}>
            <AlertCircle size={48} color="var(--error-color)" style={{ marginBottom: '16px' }} />
            <h3 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>Research Failed</h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>{error}</p>
            <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '24px' }}>
              Please try again. If the issue persists, try a different location.
            </p>
            <button 
              className="btn btn-primary" 
              onClick={() => navigate('/home')}
            >
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className="app"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="container" style={{ minHeight: '100vh', padding: '16px 20px 40px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div className="text-center" style={{ width: '100%', maxWidth: '520px' }}>
          
          {/* Header with Timer */}
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            style={{ marginBottom: '32px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '8px' }}>
              <Sparkles size={28} color="var(--primary-color)" />
              <h2 style={{ 
                fontSize: '28px', 
                fontWeight: '700',
                color: 'var(--text-primary)',
                margin: 0
              }}>
                Researching Location
              </h2>
            </div>
            <p style={{ 
              fontSize: '16px',
              color: 'var(--text-secondary)',
              margin: '0 0 16px 0'
            }}>
              Creating your comprehensive historical tour
            </p>
            
            {/* Elapsed Time */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: 'var(--card-background)',
              padding: '8px 16px',
              borderRadius: '20px',
              border: '1px solid var(--border-color)'
            }}>
              <Clock size={16} color="var(--primary-color)" />
              <span style={{ 
                fontSize: '16px', 
                fontWeight: '600',
                color: 'var(--text-primary)',
                fontFamily: 'monospace'
              }}>
                {formatTime(elapsedTime)}
              </span>
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                elapsed
              </span>
            </div>
          </motion.div>

          {/* Steps List */}
          <div style={{ marginBottom: '32px' }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <AnimatePresence mode="sync">
                {steps.map((step, index) => (
                  <motion.div
                    key={step.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ 
                      delay: 0.15 + index * 0.08, 
                      duration: 0.35, 
                      ease: [0.25, 0.1, 0.25, 1]
                    }}
                    style={{
                      backgroundColor: 'var(--card-background)',
                      borderRadius: '12px',
                      padding: '16px',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '14px',
                      willChange: 'opacity, transform',
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                      WebkitTransform: 'translate3d(0, 0, 0)',
                      transform: 'translate3d(0, 0, 0)',
                      ...getStepStyles(index)
                    }}
                  >
                  {/* Step Icon */}
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    ...getIconStyles(index),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'all 0.3s ease'
                  }}>
                    {completedSteps.includes(index) ? <Check size={20} /> : step.icon}
                  </div>

                  {/* Step Content */}
                  <div style={{ flex: 1 }}>
                    <h3 style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      margin: '0 0 2px 0'
                    }}>
                      {step.title}
                    </h3>
                    <p style={{
                      fontSize: '13px',
                      color: 'var(--text-secondary)',
                      margin: 0,
                      lineHeight: '1.4'
                    }}>
                      {step.description}
                    </p>
                  </div>

                  {/* Active indicator */}
                  {getStepState(index) === 'active' && (
                    <motion.div
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--primary-color)'
                      }}
                    />
                  )}
                </motion.div>
              ))}
              </AnimatePresence>
            </div>
          </div>

          {/* Footer Message */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.4 }}
            style={{ 
              backgroundColor: 'rgba(99, 102, 241, 0.05)',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '16px'
            }}
          >
            <p style={{ 
              fontSize: '13px',
              color: 'var(--text-secondary)',
              margin: 0
            }}>
              💡 Usually takes around <strong>90 seconds</strong> to research a tour. You can safely leave this page.
            </p>
          </motion.div>

          {/* Back to Collection Button */}
          {pendingTourId && (
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.4 }}
              onClick={() => navigate('/past-tours')}
              style={{
                width: '100%',
                padding: '14px 20px',
                backgroundColor: 'var(--card-background)',
                color: 'var(--text-primary)',
                border: '2px solid var(--border-color)',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--primary-color)';
                e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-color)';
                e.currentTarget.style.backgroundColor = 'var(--card-background)';
              }}
            >
              <ArrowLeft size={20} />
              Back To My Collection
            </motion.button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default GeneratingTour;
